/**
 * Der Sicherheitscode, mit dem eine E-Mail-Adresse bewiesen wird (HM-060).
 *
 * **Warum ein Code und kein Link.** Der Bestätigungslink aus HM-026 geht an
 * eine Adresse und wird dort geöffnet — er beweist den Zugriff auf das Postfach
 * und sonst nichts. Der Code beweist ihn **gegenüber dem Browser, der gerade
 * registriert**: Nur wer beides in der Hand hat, bringt den Vorgang zu Ende.
 * Das ist der Unterschied zwischen „diese Adresse existiert" und „die Person
 * vor diesem Formular liest diese Adresse". Und ein Link, der ein Konto anlegt,
 * ließe sich aus einem fremden Postfach heraus anklicken.
 *
 * **Sechs Ziffern sind ein Passwort, kein Zufallswert.** Eine Million
 * Möglichkeiten sind in Sekunden durchprobiert; was den Code trägt, ist
 * ausschließlich, dass es nur wenige Versuche gibt und er kurz lebt. Beide
 * Zahlen stehen deshalb hier und nicht verstreut im Dienst — wer eine davon
 * ändert, ändert die Sicherheitsaussage des Verfahrens.
 *
 * Alles hier ist reine Rechnung: keine Uhr, kein Zufall, keine Datenbank. Die
 * Zufallsquelle bleibt beim Aufrufer — dieses Package kennt kein `node:crypto`,
 * und ein `Math.random` an dieser Stelle wäre der teuerste Einzeiler des
 * Produkts.
 */

/** Stellen des Codes. Steht im Vertrag und in der Nachricht — eine Zahl. */
export const EMAIL_CODE_DIGITS = 6;

/** Zahl der möglichen Codes: `10 ** EMAIL_CODE_DIGITS`. */
export const EMAIL_CODE_RANGE = 10 ** EMAIL_CODE_DIGITS;

/**
 * Wie lange ein Code gilt.
 *
 * **Zehn Minuten, nicht eine Stunde.** Er wird abgetippt, während der Absender
 * vor dem Formular sitzt; alles darüber verlängert nur das Fenster, in dem ein
 * abgefangener Code noch etwas wert ist. Wer länger braucht, fordert einen
 * neuen an — das kostet einen Klick und keine Daten.
 */
export const EMAIL_CODE_LIFETIME_SECONDS = 10 * 60;

/**
 * So oft darf geraten werden, bevor der Vorgang verfällt.
 *
 * Fünf Versuche gegen eine Million Möglichkeiten: Die Chance liegt bei 1 zu
 * 200 000, und sie steigt nicht, weil ein sechster Versuch den Vorgang
 * **beendet** statt ihn zu bremsen. Eine Ratenbegrenzung allein täte das nicht
 * — sie verteilt die Versuche nur über die Zeit, und der Code lebt zehn
 * Minuten.
 */
export const EMAIL_CODE_MAX_ATTEMPTS = 5;

/**
 * Wartezeit bis zum nächsten Versand.
 *
 * Sie gehört der **Oberfläche** — sie sagt, wann der Knopf wieder etwas tut.
 * Was den Missbrauch begrenzt, ist das Kontingent am Endpunkt: Ein Wert, den
 * der Client mitbringt, ist kein Schutz, weil ein Angreifer keinen Client
 * benutzt.
 */
export const EMAIL_CODE_RESEND_COOLDOWN_SECONDS = 60;

/**
 * Wie lange eine offene Registrierung überhaupt aufbewahrt wird.
 *
 * Sie trägt einen Passwort-Hash zu einer Adresse, deren Besitzer nichts
 * bestätigt hat. Nach Ablauf des Codes ist sie wertlos; sie danach noch tagelang
 * zu halten, sammelt Angaben von Menschen, die es sich anders überlegt haben.
 * Der Puffer über die Codelaufzeit hinaus lässt Raum für einen zweiten Anlauf
 * mit denselben Angaben.
 */
export const PENDING_REGISTRATION_MAX_AGE_SECONDS = 24 * 60 * 60;

/**
 * Bringt eine Zufallszahl in die Form, die in der Nachricht steht.
 *
 * **Mit führenden Nullen.** `000042` ist ein gültiger Code; ihn als `42` zu
 * schreiben, machte aus sechs Stellen fünf und aus einer Million Möglichkeiten
 * neunhunderttausend — und der Empfänger fragte sich, wo die anderen Ziffern
 * geblieben sind.
 */
export function formatEmailCode(value: number): string {
  if (!Number.isInteger(value) || value < 0 || value >= EMAIL_CODE_RANGE) {
    throw new RangeError(
      `Ein Sicherheitscode liegt zwischen 0 und ${EMAIL_CODE_RANGE - 1}, nicht ${value}.`,
    );
  }

  return String(value).padStart(EMAIL_CODE_DIGITS, "0");
}

/** Genau sechs Ziffern, sonst nichts. Kein Leerraum, kein Bindestrich. */
export function isWellFormedEmailCode(value: string): boolean {
  return new RegExp(`^\\d{${EMAIL_CODE_DIGITS}}$`).test(value);
}

/** Der Zustand einer offenen Registrierung, soweit er für die Prüfung zählt. */
export interface EmailCodeState {
  /** Wann der Code ungültig wird. */
  readonly expiresAt: Date;
  /** Wie oft schon falsch geraten wurde. */
  readonly attempts: number;
}

/**
 * Darf dieser Code überhaupt noch geprüft werden?
 *
 * `expired` und `exhausted` sind getrennt, weil sie verschiedene Ursachen haben
 * — nach außen führen sie zur **selben** Antwort. Der Unterschied nützt nur dem,
 * der rät; im Protokoll erklärt er, warum ein Vorgang endete.
 */
export type EmailCodeCheck = "acceptable" | "expired" | "exhausted";

export function checkEmailCodeUsable(state: EmailCodeState, now: Date): EmailCodeCheck {
  if (state.attempts >= EMAIL_CODE_MAX_ATTEMPTS) return "exhausted";
  if (state.expiresAt.getTime() <= now.getTime()) return "expired";
  return "acceptable";
}

/**
 * Wie viele Fehlversuche nach diesem noch bleiben.
 *
 * Steht hier, damit die Zahl an einer Stelle gerechnet wird — **nicht**, damit
 * sie in eine Antwort geht: Ein mitgeteilter Zähler sagt dem Aufrufer, dass es
 * diesen Vorgang gibt, und damit, dass die Adresse noch frei war.
 */
export function attemptsLeft(attempts: number): number {
  return Math.max(0, EMAIL_CODE_MAX_ATTEMPTS - attempts);
}

/** Ablauf eines frisch versendeten Codes, gerechnet vom Versandzeitpunkt. */
export function emailCodeExpiryFrom(sentAt: Date): Date {
  return new Date(sentAt.getTime() + EMAIL_CODE_LIFETIME_SECONDS * 1000);
}

/** Wann der nächste Versand angeboten wird. */
export function emailCodeResendFrom(sentAt: Date): Date {
  return new Date(sentAt.getTime() + EMAIL_CODE_RESEND_COOLDOWN_SECONDS * 1000);
}
